<template>
  <div class="taxSettingsForm">
    <div v-if="loading">Loading...</div>
    <div v-else>
      <div class="d-flex mb-2">
        <Select
          v-model="formData.taxability"
          data-cy="taxability-dropdown"
          :options="taxabilityOptions"
          class="full-width mr-3"
          :allow-empty="false"
          :field-error="firstErrorMessage(v$.taxability)"
          @closed="handleTaxabilityChange"
        >
          <template #label>
            <div style="position: relative">
              <span>Taxability</span>
            </div>
          </template>
        </Select>
        <Select
          v-if="!showNoTaxZonesWarning"
          v-model="formData.tax_zone_id"
          :allow-empty="false"
          data-cy="tax-zones-dropdown"
          label="Tax Zone"
          :field-error="firstErrorMessage(v$.tax_zone_id)"
          :options="optionsTaxZones"
          class="full-width"
          :disabled="!canSelectTaxZone"
          :placeholder="!canSelectTaxZone ? 'Not applicable' : 'Select...'"
        >
          <template #label>
            <span>
              Tax Zone
              <span v-if="canSelectTaxZone" class="sera-color-text-error"
                >*</span
              >
            </span>
          </template>
        </Select>
        <div v-else class="full-width ml-2">
          <div class="label">Tax Zone</div>
          <Alert
            class="no-tax-zones-warning"
            data-cy="no-tax-zones-setup-message"
            text="No tax zones available"
          ></Alert>
        </div>
      </div>
      <div v-if="taxExemptionReasonsVisible">
        <Select
          v-if="optionsTaxExemptionReasons.length"
          v-model="formData.reason"
          class="full-width mb-2"
          :field-error="firstErrorMessage(v$.reason)"
          data-cy="tax-exempt-reason-dropdown"
          :options="optionsTaxExemptionReasons"
        >
          <template #label>
            <span>
              Reason for exemption
              <span class="sera-color-text-error">*</span>
            </span>
          </template>
        </Select>
        <div v-else>
          <div class="label">Reason for exemption</div>
          <Alert
            data-cy="no-tax-reasons-setup-message"
            text="No tax exemption reasons available"
          ></Alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import {
  TaxSettingsData,
  TaxSettingsDefaults,
  TaxSettingsFormData
} from "@/typings/taxability";
import { TaxSettingsContextTypes } from "@/typings/taxability";
import useVuelidate from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";

/**
 * Notes:
 * - If data is passed in, we will assume we are editing an existing taxSetting
 * - TaxDefault form values are what populate the form values on init
 * - Tax Zone ID is based on the Company setting
 * - Taxability is based on the form value
 * - Tax reason is carried over from the form value and auto selected (only for tax-exempt flows)
 * - If you choose "Taxable" and we have a company default tax zone, we'll use that
 * - Tax zones are disabled when the taxability is not taxable
 * - Tax exempt is the only flow you'll see a reason input
 * - Taxability does not need a (default) text
 */

const props = defineProps({
  data: {
    type: Object as () => TaxSettingsData,
    default: null,
    required: false
  },
  context: {
    type: [String, Number, Object],
    default: null,
    required: false
  },
  contextType: {
    type: String as () => TaxSettingsContextTypes,
    default: null,
    required: true
  }
});

const store = useStore();
const loading = ref(true);
const taxabilityOptions = ref<any[]>([]);
const companyTaxDefaults = ref<TaxSettingsDefaults>();
const formData = ref<TaxSettingsFormData>({
  id: null,
  taxability: null,
  reason: "",
  tax_zone_id: null
});

const v$ = useVuelidate(
  {
    taxability: {
      required: helpers.withMessage("No tax zones available", () => {
        if (formData.value.taxability !== "taxable") return true;

        return optionsTaxZones.value.length >= 1;
      })
    },
    tax_zone_id: {
      required: helpers.withMessage(
        "Tax Zone is required",
        requiredIf(() => formData.value.taxability === "taxable")
      )
    },
    reason: {
      required: helpers.withMessage(
        "Tax exemption reason is required",
        requiredIf(() => formData.value.taxability === "tax_exempt")
      )
    }
  },
  formData.value
);

const taxExemptionReasonsVisible = computed(() => {
  return formData.value.taxability === "tax_exempt";
});

const optionsTaxZones = computed(() => {
  return store.state.taxability.companyTaxZones.map((taxZone: any) => {
    const isDefault = taxZone.id === companyTaxDefaults.value?.tax_zone_id;
    return {
      label: taxZone.formatted_name + (isDefault ? " (default)" : ""),
      value: taxZone.id
    };
  });
});

// Note: Do not use the ID of the reason. We will always send the text value.
const optionsTaxExemptionReasons = computed(() => {
  return store.state.taxability.companyTaxExemptionReasons.map(
    (exemptReason: any) => {
      return {
        value: exemptReason.reason,
        label: exemptReason.reason
      };
    }
  );
});

const canSelectTaxZone = computed(() => {
  return (
    formData.value.taxability === "taxable" ||
    formData.value.taxability === "not_set"
  );
});

const showNoTaxZonesWarning = computed(() => {
  return (
    optionsTaxZones.value.length === 0 &&
    formData.value.taxability === "taxable"
  );
});

async function validate() {
  return v$.value.$validate();
}

async function getFormData() {
  const valid = await validate();
  if (!valid) throw "Invalid form";
  return {
    ...formData.value,
    reason:
      formData.value.taxability === "tax_exempt"
        ? formData.value.reason
        : undefined,
    id: props.data?.id || null
  };
}

function selectReason(reason?: string | null) {
  if (!reason) {
    formData.value.reason = null;
  }
  const reasonFound = optionsTaxExemptionReasons.value.find((option) => {
    return option.label === reason;
  });
  if (reasonFound) {
    formData.value.reason = reasonFound.value;
  }
}

function findTaxZoneId(name: string) {
  const taxZone = optionsTaxZones.value.find(({ label }) => {
    return label.includes(name);
  });
  return taxZone?.value;
}

// If the user selects "taxable" and we have a default for tax zone, we should use it
function handleTaxabilityChange() {
  if (!companyTaxDefaults.value?.tax_zone_id) return;
  if (formData.value.taxability === "taxable") {
    formData.value.tax_zone_id = companyTaxDefaults.value.tax_zone_id;
  }
}

onMounted(async () => {
  try {
    const taxDefaults = await store.dispatch("getDefaultTaxSettings", {
      isNew: !props.data,
      context: props.context,
      contextType: props.contextType
    });
    if (!store.state.taxability.companyTaxZones.length) {
      await store.dispatch("fetchCompanyTaxZones");
    }
    if (!store.state.taxability.companyTaxExemptionReasons.length) {
      await store.dispatch("fetchCompanyTaxExemptionReasons");
    }
    if (props.data) {
      formData.value.taxability = props.data.taxability;
      formData.value.tax_zone_id =
        props.data.tax_zone_id ||
        findTaxZoneId(props.data.tax_zone_formatted_name);
      selectReason(props.data.reason);
    } else {
      formData.value.taxability = taxDefaults.formValues.taxability;
      formData.value.tax_zone_id = taxDefaults.companyDefaults.tax_zone_id;
      selectReason(taxDefaults.formValues.reason);
    }
    companyTaxDefaults.value = taxDefaults.companyDefaults;
    taxabilityOptions.value = taxDefaults.taxabilityOptions;
  } finally {
    loading.value = false;
  }
});

// When props change, update the formData
watch(
  () => JSON.stringify(props.data),
  (newProps, oldProps) => {
    if (newProps === oldProps) return;
    Object.assign(formData.value, props.data);
  }
);

// Disable tax zone if user didn't select "taxable"
watch(
  () => [canSelectTaxZone.value, formData.value.taxability],
  () => {
    if (!canSelectTaxZone.value) {
      formData.value.tax_zone_id = null;
    }
  },
  { immediate: true }
);

defineExpose({ validate, getFormData, optionsTaxZones });
</script>

<style lang="scss" scoped>
.no-tax-zones-warning {
  margin-top: 2px;
}
</style>
